import { DesktopOS } from './desktop-os.enum';
import { MobileOS } from './mobile-os.enum';
import { Nullable } from '../../interfaces';
import { computed, Injectable, signal } from '@angular/core';

/**
 * Service for detecting device types and operating systems based on User-Agent.
 * Provides reactive signals for device type (mobile, tablet, desktop) and OS detection.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceDetectorService {
  /**
   * Signal that holds the user agent string
   * @private
   */
  private readonly userAgent = signal<Nullable<string>>(navigator?.userAgent || navigator?.vendor || null);

  /**
   * Whether the device is mobile (smartphone).
   * Returns `false` on the server.
   * @returns {Signal<boolean>} Signal that returns `true` if the device is mobile, `false` otherwise.
   */
  public readonly isMobile = computed(() => this.isMobileDevice(this.userAgent()));

  /**
   * Whether the device is a tablet.
   * Returns `false` on the server.
   */
  public readonly isTablet = computed(() => this.isTabletDevice(this.userAgent()));

  /**
   * Whether the device is a desktop (neither mobile nor tablet).
   * Returns `true` on the server by default.
   */
  public readonly isDesktop = computed(() => !this.isMobile() && !this.isTablet());

  /**
   * Detected mobile OS (`Android`, `iOS`, `WindowsPhone`, or `Unknown`).
   * Returns `null` on the server or if not a mobile device.
   */
  public readonly mobileOS = computed(() => this.getMobileOS(this.userAgent(), this.isMobile()));

  /**
   * Detected desktop OS (`Windows`, `MacOS`, `Unix`, `Linux`, or `Unknown`).
   * Returns `null` on the server or if not a desktop device.
   */
  public readonly desktopOS = computed(() => this.getDesktopOS(this.userAgent(), this.isDesktop()));

  /**
   * Whether the platform uses Apple keyboard conventions (⌘ as the primary
   * shortcut modifier): macOS desktops and iOS devices, iPads included.
   * Returns `false` on the server and on Windows/Linux/Unix.
   */
  public readonly isApplePlatform = computed(() => {
    if (this.desktopOS() === DesktopOS.MacOS || this.mobileOS() === MobileOS.iOS) {
      return true;
    }

    // Tablets report no mobileOS — recognize iPads by their user agent.
    const userAgent = this.userAgent();
    return Boolean(userAgent && this.isTablet() && /iPad/i.test(userAgent));
  });

  private isMobileDevice(userAgent: Nullable<string>): boolean {
    if (!userAgent) return false;

    const regexes = [/(Android)(.+)(Mobile)/i, /BlackBerry/i, /iPhone|iPod/i, /Opera Mini/i, /IEMobile/i];
    return regexes.some(b => userAgent.match(b));
  }

  private isTabletDevice(userAgent: Nullable<string>): boolean {
    if (!userAgent) return false;

    const regexes =
      /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/;
    return regexes.test(userAgent.toLowerCase());
  }

  private getMobileOS(userAgent: Nullable<string>, isMobileDevice?: boolean): MobileOS | null {
    if (userAgent && isMobileDevice) {
      if (/windows phone/i.test(userAgent)) return MobileOS.WindowsPhone;
      else if (/android/i.test(userAgent)) return MobileOS.Android;
      else if (/iPad|iPhone|iPod/.test(userAgent) && !('MSStream' in window)) return MobileOS.iOS;

      return MobileOS.Unknown;
    }

    return null;
  }

  private getDesktopOS(userAgent: Nullable<string>, isDesktopDevice?: boolean): DesktopOS | null {
    if (userAgent && isDesktopDevice) {
      if (~userAgent.indexOf('Win')) return DesktopOS.Windows;
      else if (~userAgent.indexOf('Mac')) return DesktopOS.MacOS;
      else if (~userAgent.indexOf('X11')) return DesktopOS.Unix;
      else if (~userAgent.indexOf('Linux')) return DesktopOS.Linux;

      return DesktopOS.Unknown;
    }

    return null;
  }
}
